import React, { useState, useEffect } from "react";
import { GenericButton } from "../../components/common/GenericButton";
import { Field, Form, FieldError } from "react-jsonschema-form-validation";
import {
	fromUnixTime,
	millisecondsToSeconds,
	secondsToMilliseconds,
	getYear,
	getDaysInMonth,
	getDate,
	getMonth,
	getUnixTime,
} from "date-fns";
import _ from "lodash";
import { getMonthAbbreviation } from "../../services/dateService";
import { Select } from "../../components/common/Select";
import { SimpleSelect } from "../../components/common/SimpleSelect";
import { sanitize } from "dompurify";
import { FormValidator } from "../../components/common/FormValidator";
import phone from "phone";
import { getList } from "../../services/dashApiService";
import { toast } from "react-toastify";

export function PersonalDetails({ onDataComplete, context, data }) {
	const sexOptions = [
		{
			label: "Male",
			value: "Male",
			id: "male",
		},
		{
			label: "Female",
			value: "Female",
			id: "female",
		},
		{
			label: "Prefer Not to Say",
			value: "PreferNotToSay",
			id: "preferNotToSay",
		},
	];

	const schema = {
		type: "object",
		properties: {
			firstName: { type: "string", maxLength: 64 },
			surname: { type: "string", maxLength: 64 },
			postalCode: { type: "string", minLength: 6, maxLength: 8 },
			diseaseGroup: {
				type: "object",
				properties: { value: { type: "string" }, label: { type: "string" } },
			},
		},
		required: [
			"firstName",
			"surname",
			"sex",
			"postalCode",
			"dob",
			"diseaseGroup",
		],
	};

	const formFieldsDefault = {
		firstName: (data && data.firstName) ?? "",
		surname: (data && data.surname) ?? "",
		sex: (data && data.sex) ?? "PreferNotToSay",
		mobilePhone: (data && data.mobilePhone) ?? "",
		postalCode: (data && data.postalCode) ?? "",
		diseaseGroup: (data && data.diseaseGroup) ?? {
			value: "",
			label: "Select a type of cancer",
		},
		dob:
			data && data.dob
				? fromUnixTime(millisecondsToSeconds(data.dob))
				: Date.now(),
	};

	const [formData, setFormData] = useState(formFieldsDefault);
	const [diseaseGroups, setDiseaseGroups] = useState([]);
	const [dgList, setDgList] = useState([]);

	const setFormField = (value, propertyName) => {
		let updatedData = { ...formData };
		updatedData[propertyName] = value;
		setFormData(updatedData);
	};

	const years = _.range(1900, getYear(Date.now()));
	const yearsOptions = years.map((y) => {
		return {
			id: y,
			label: y,
			value: y,
		};
	});

	const months = _.range(12);
	const monthsOptions = months.map((m) => {
		return {
			id: m,
			label: getMonthAbbreviation(m),
			value: m,
		};
	});

	const [daysInMonth, setDaysInMonth] = useState(
		getDaysInMonth(fromUnixTime(millisecondsToSeconds(data ? data.dob : null)))
	);

	const days = _.range(1, daysInMonth + 1);
	const dayOptions = days.map((d) => {
		return {
			id: d,
			label: d,
			value: d,
		};
	});

	const [dateOfBirth, setDateOfBirth] = useState(
		fromUnixTime(millisecondsToSeconds(data ? data.dob : null))
	);

	const initialDate = dayOptions.find(
		(date) => date.value === getDate(dateOfBirth)
	);
	const initialMonth = monthsOptions.find(
		(month) => month.value === getMonth(dateOfBirth)
	);
	const initialYear = yearsOptions.find(
		(year) => year.value === getYear(dateOfBirth)
	);
	const [currentDateOption, setCurrentDateOption] = useState(initialDate);
	const [currentMonthOption, setCurrentMonthOption] = useState(initialMonth);
	const [currentYearOption, setCurrentYearOption] = useState(initialYear);

	useEffect(() => {
		const d = new Date(
			Date.UTC(
				currentYearOption.value,
				currentMonthOption.value,
				currentDateOption.value
			)
		);
		setDateOfBirth(d);
	}, [currentDateOption, currentMonthOption, currentYearOption]);

	useEffect(() => {
		const d = secondsToMilliseconds(getUnixTime(dateOfBirth));
		let updatedData = { ...formData };
		if (d !== updatedData.dob) {
			updatedData.dob = d;
			setFormData(updatedData);
		}
	}, [dateOfBirth, formData]);

	useEffect(() => {
		const d = getDaysInMonth(
			new Date(Date.UTC(currentYearOption.value, currentMonthOption.value))
		);
		setDaysInMonth(d);
		if (currentDateOption.value > d) {
			setCurrentDateOption(dayOptions.find((date) => date.value === 1));
		}
	}, [
		currentMonthOption,
		currentYearOption,
		currentDateOption.value,
		dayOptions,
	]);

	useEffect(() => {
		const text = (
			<>
				<p className="mb-2">There was a problem connecting to our server.</p>
				<p className="mb-2">Please press "Back" and try again.</p>
			</>
		);

		let aborted = false;

		getList("diseasegroup", "", "Select a type of cancer")
			.then((result) => {
				if (aborted) {
					return;
				}

				if (result.success) {
					const dgs = [];

					for (const r of result.data.list) {
						dgs.push({ value: r.value, label: r.cancerName || r.display });
					}

					setDiseaseGroups(dgs);
					setDgList(result.data.list);
				} else {
					toast(text, {
						autoClose: false,
						hideProgressBar: true,
						type: "error",
					});
				}
			})
			.catch(() => {
				if (aborted) {
					return;
				}

				toast(text, { autoClose: false, hideProgressBar: true, type: "error" });
			});

		return () => {
			aborted = true;
		};
	}, []);

	const getDGAdditional = (dg) => {
		let ret = {
			email: "help@health-companion.me",
			telephone: "0116 218 5718",
			longName: "Your Health Companion",
		};

		for (const d of dgList) {
			if (d.value === dg.value) {
				ret.email = d.email || ret.email;
				ret.telephone = d.telephone || ret.telephone;
				ret.longName = d.longName || ret.longName;
				break;
			}
		}

		return ret;
	};

	const handleSaveClick = () => {
		onDataComplete(context, {
			firstName: formData.firstName,
			surname: formData.surname,
			sex: formData.sex,
			mobilePhone: formData.mobilePhone,
			postalCode: formData.postalCode,
			dob: formData.dob,
			diseaseGroup: formData.diseaseGroup,
			diseaseGroupContact: getDGAdditional(formData.diseaseGroup),
			dobStr: `${currentDateOption.label} ${currentMonthOption.label} ${currentYearOption.label}`,
		});

		const text = (
			<>
				<p className="mb-2">Your personal details have been saved.</p>
				<p className="mb-2">Now record your consent to use the app.</p>
			</>
		);

		toast(text, {
			type: "success",
		});
	};

	const sexElements = sexOptions.map((e) => {
		const element = (
			<>
				<Field
					type="radio"
					name="sex"
					key={e.id}
					id={e.id}
					className={"hidden radioOption"}
					value={e.value}
					checked={formData.sex === e.value}
					onClick={() => setFormField(e.value, "sex")}
				/>
				<label
					htmlFor={e.id}
					className={`option ${
						formData.sex === e.value
							? `bg-black text-white font-medium`
							: "bg-gray-100"
					}`}
				>
					{e.label}
				</label>
			</>
		);

		return element;
	});

	const [validatorIsValid, setValidatorIsValid] = useState(false);

	const [phoneNumberValid, setPhoneNumberValid] = useState(true);
	const onChangePhoneNumber = (e) => {
		const value = e.target.value;
		if (value) {
			const check = phone(value);
			if (check.isValid === true) {
				setPhoneNumberValid(true);
			} else {
				const ukCheck = phone(value, { country: "GBR" });
				setPhoneNumberValid(ukCheck.isValid);
			}

			setFormField(sanitize(value), "mobilePhone");
		} else {
			setFormField("", "mobilePhone");
			setPhoneNumberValid(true);
		}
	};

	const onChangeDiseaseGroup = (sel) => {
		setFormField(sel, "diseaseGroup");
	};

	const handleValidate = (valid) => {
		setValidatorIsValid(valid);
	};

	return (
		<div className={"text-sm lg:text-base"}>
			<h2 className={"text-2xl font-bold mb-2 text-center"}>
				Personal Details
			</h2>
			<p
				className={
					"mb-4 sm:mb-6 text-sm lg:text-base leading-tight lg:leading-6"
				}
			>
				Please enter some basic details about yourself so that the system can
				recognise you. We use the data for communication with you. We do not use
				patient identifiable data in our research.
			</p>

			<Form
				schema={schema}
				data={formData}
				errorMessages={{
					required: () => "You must fill out all required fields",
				}}
			>
				<div className={"max-w-lg mx-auto overflow-y-visible h-fit"}>
					<div className={"mb-4 text-sm"}>
						<label className={"basicFormLabel"} htmlFor={"dg"}>
							Cancer type<sup>*</sup>
						</label>
						<div className="w-full">
							<Field
								name="diseaseGroup"
								id="diseaseGroup"
								key="diseaseGroup"
								options={diseaseGroups}
								value={formData.diseaseGroup}
								onChange={onChangeDiseaseGroup}
								classes="w-full"
								component={SimpleSelect}
							/>
						</div>
					</div>

					<div className={"mb-4 text-sm"}>
						<label className={"basicFormLabel"} htmlFor={"firstName"}>
							First Name<sup>*</sup>
						</label>
						<div className="w-full">
							<Field
								type="text"
								key="firstName"
								className={"formField z-0"}
								id={"firstName"}
								name="firstName"
								placeholder={"First Name"}
								value={formData.firstName}
								onChange={(e) =>
									setFormField(sanitize(e.target.value), "firstName")
								}
							/>
							<FieldError
								errorMessages={{
									required: () => "You must fill out all required fields",
								}}
								name={"firstName"}
								className={"rounded-b-sm z-0"}
							/>
						</div>
					</div>

					<div className={"mb-4 text-sm"}>
						<label className={"basicFormLabel"} htmlFor={"surname"}>
							Surname<sup>*</sup>
						</label>
						<div className="w-full">
							<Field
								type="text"
								key="surname"
								className={"formField z-0"}
								id={"surname"}
								name="surname"
								placeholder={"Surname"}
								value={formData.surname}
								onChange={(e) =>
									setFormField(sanitize(e.target.value), "surname")
								}
							/>
							<FieldError
								errorMessages={{
									required: () => "You must fill out all required fields",
								}}
								name={"surname"}
								className={"rounded-b-sm z-0"}
							/>
						</div>
					</div>

					<div className={"mb-4 text-sm"}>
						<label className={"basicFormLabel"} htmlFor={"dob"}>
							Date of Birth<sup>*</sup>
						</label>
						<div className="flex-grow  sm:gap-4 gap-2 flex">
							<div className="w-3/4">
								<Select
									options={dayOptions}
									selectedOption={currentDateOption}
									handleChange={(value) => {
										setCurrentDateOption(
											dayOptions.find((day) => day.id === value)
										);
									}}
									bg={"bg-gray-50"}
								/>
							</div>
							<div className="w-3/4">
								<Select
									options={monthsOptions}
									selectedOption={currentMonthOption}
									handleChange={(value) => {
										setCurrentMonthOption(
											monthsOptions.find((month) => month.id === value)
										);
									}}
									bg={"bg-gray-50"}
								/>
							</div>
							<div className="w-full">
								<Select
									options={yearsOptions}
									selectedOption={currentYearOption}
									handleChange={(value) => {
										setCurrentYearOption(
											yearsOptions.find((year) => year.id === value)
										);
									}}
									bg={"bg-gray-50"}
								/>
							</div>
						</div>
					</div>

					<div className={"mb-4 text-sm"}>
						<label className={"basicFormLabel"} htmlFor={"postalCode"}>
							Postcode<sup>*</sup>
						</label>
						<div className="w-full">
							<Field
								type="text"
								key="postalCode"
								className={"formField z-0"}
								id={"postalCode"}
								name="postalCode"
								placeholder={"Postcode"}
								value={formData.postalCode}
								onChange={(e) =>
									setFormField(sanitize(e.target.value), "postalCode")
								}
							/>
							<FieldError
								errorMessages={{
									required: () => "You must fill out all required fields",
								}}
								name={"postalCode"}
								className={"rounded-b-sm z-0"}
							/>
						</div>
					</div>

					<div className={"mb-4 text-sm"}>
						<label className={"basicFormLabel"} htmlFor={"mobilePhone"}>
							Mobile No.
						</label>
						<div className="w-full">
							<input
								key="mobilePhone"
								type="text"
								className={"formField z-0"}
								id={"mobilePhone"}
								name="mobilePhone"
								placeholder={"Mobile Phone"}
								value={formData.mobilePhone}
								onChange={onChangePhoneNumber}
							/>
							{phoneNumberValid ? null : (
								<div className={"Jfv_FieldError isTouched rounded-sm z-0"}>
									Phone number is not valid
								</div>
							)}
						</div>
					</div>

					<div className={"mb-4 text-sm"}>
						<label className={"basicFormLabel"} htmlFor={"sex"}>
							Sex
						</label>
						<div className={"sm:flex mb-3"}>
							<div className={`radioSlider`}>{sexElements}</div>
						</div>
					</div>
				</div>
				<FormValidator validateCallback={handleValidate} />
			</Form>

			<div className={"flex justify-center max-w-xs mx-auto pt-2"}>
				<div className={"w-1/2"}>
					<GenericButton
						text="Save"
						disabled={!validatorIsValid || formData.diseaseGroup.value === ""}
						onClick={handleSaveClick}
						classes="active-button"
					/>
				</div>
			</div>
		</div>
	);
}
